import { useState, useRef, useEffect, ChangeEvent, MutableRefObject } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Input, Modal, Button, InputField } from '@ets/design-system';

import { useDispatch, useSelector } from '_common/hooks';

import { renameObject, editDescription } from 'App/redux/appSlice';
import { closeAndResetModal } from '_common/modals/ModalsSlice';
import {
  useEditRoleDescriptionMutation,
  useRenameRoleMutation,
} from 'Settings/pages/TenantSettingsPage/Roles/RolesApi';
import { useEditProfileMutation } from 'Settings/pages/UserSettingsPage/UserSettingsApi';
import { paths } from '_types/authority';

import { Textarea, type TextareaAPI } from '_common/lib/components';

const MODAL = 'GenericSettingModal';

type ValueProps = {
  value: string;
};

export type ActionProps = {
  rename: Omit<Parameters<typeof renameObject>[0], 'newName'> & ValueProps;
  editDescription: Omit<Parameters<typeof editDescription>[0], 'newDescription'> & ValueProps;
  renameRole: Pick<Role, 'id'> & { value: Role['name'] };
  editRoleDescription: Pick<Role, 'id'> & { value: Role['description'] };
  editProfile: {
    setting: keyof paths['/api/authority/user/profile/edit']['post']['requestBody']['content']['multipart/form-data'];
  } & ValueProps & { userId?: UserId };
};

export type Action = keyof ActionProps;
const GenericSettingModal = () => {
  const intl = useIntl();

  const [mutRenameRole] = useRenameRoleMutation();
  const [mutEditRoleDescription] = useEditRoleDescriptionMutation();
  const [editProfile] = useEditProfileMutation();

  const ACTIONS: { [key in Action]: (props: ActionProps[key]) => void } = {
    rename: ({ objectId, objectType, value }) => {
      dispatch(renameObject({ objectId, objectType, newName: value }));
    },
    editDescription: ({ objectId, objectType, value }) => {
      dispatch(editDescription({ objectId, objectType, newDescription: value }));
    },
    renameRole: ({ id, value }) => {
      mutRenameRole({ id, name: value });
    },
    editRoleDescription: ({ id, value }) => {
      mutEditRoleDescription({ id, description: value });
    },
    editProfile: ({ setting, value, userId }) => {
      editProfile({ [setting]: value, userId });
    },
  };

  // redux
  const dispatch = useDispatch();
  const isOpen = useSelector((state) => state.modals.open[MODAL]);
  const inputType = useSelector((state) => state.modals[MODAL].inputType);
  const header = useSelector((state) => state.modals[MODAL].header);
  const label = useSelector((state) => state.modals[MODAL].label);
  const value = useSelector((state) => state.modals[MODAL].value);
  const inputPlaceholder = useSelector((state) => state.modals[MODAL].inputPlaceholder);
  const buttons = useSelector((state) => state.modals[MODAL].buttons);
  const data = useSelector((state) => state.modals[MODAL].data);
  const type = useSelector((state) => state.modals[MODAL].type);
  const action = useSelector((state) => state.modals[MODAL].action);
  const actionValues = useSelector((state) => state.modals[MODAL].actionValues);

  // local
  const [inputValue, setInputValue] = useState(value);
  const [validations, setValidations] = useState<{ input?: string }>({});
  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<TextareaAPI>(null);
  const typedAction = action in ACTIONS ? (action as Action) : undefined;

  useEffect(() => {
    if (isOpen) {
      setInputValue(value);

      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
        if (textareaRef.current) {
          textareaRef.current.focus();
        }
      }, 0);
    }
  }, [isOpen, value, data, type]);

  const close = () => {
    setInputValue('');
    setValidations({});
    dispatch(closeAndResetModal(MODAL));
  };

  const handleInputChange = (inputName: string) => {
    const newValidations: typeof validations = {};
    setInputValue(inputName);

    if (new RegExp(/[<>/:?*"|]/).test(inputName)) {
      newValidations.input = intl.formatMessage(
        {
          id: 'UNSUPPORTED_CHARACTER_MESSAGE',
        },
        { type: data.type },
      );
    }
    if (type === 'name') {
      if (inputName.length === 0) {
        newValidations.input = intl.formatMessage({
          id: 'validation.name.insertName',
        });
      } else if (data && (data.type === 'document' || data.type === 'file')) {
        if (inputName.length > 150) {
          newValidations.input = intl.formatMessage(
            { id: 'validation.name.longName' },
            { length: 150 },
          );
        }
      } else if (data?.type === 'group') {
        if (inputName.length > 35) {
          newValidations.input = intl.formatMessage(
            { id: 'validation.name.longName' },
            { length: 35 },
          );
        }
      } else if (inputName.length > 150) {
        newValidations.input = intl.formatMessage(
          { id: 'validation.name.longName' },
          { length: 150 },
        );
      }
    } else if (inputName.length > 1000) {
      newValidations.input = intl.formatMessage({
        id: 'validation.description.longDescription',
      });
    } else {
      newValidations.input = '';
    }

    setValidations(newValidations);
  };

  const handleOnChange = (e: ChangeEvent) => {
    const target = e.target as HTMLInputElement | HTMLTextAreaElement;
    handleInputChange(target.value);
  };

  const submit = (value: ValueProps['value']) => {
    if (typedAction && ACTIONS[typedAction]) {
      ACTIONS[typedAction]({ ...actionValues, value });
    }
  };

  const handleSubmit = () => {
    submit(inputValue);
    close();
  };

  const renderButtons = () => {
    //@ts-expect-error ModalSlice selector issue: Should be fixed once modal props have types
    return buttons?.map((button) => {
      return (
        <Button
          size="m"
          variant={button.classType || 'neutral'}
          key={button.text}
          style={button.style}
          disabled={inputValue === '' || !!validations.input}
          onClick={button.submit ? handleSubmit : button.onClick}
          testId={`generic-setting-${button.text}`}
        >
          <FormattedMessage id={button.text} />
        </Button>
      );
    });
  };

  return (
    <Modal width="75rem" open={!!isOpen} close={close} testId="generic-setting">
      <Modal.Header>{header}</Modal.Header>
      <Modal.Body>
        {inputType === 'textarea' ? (
          <InputField
            size="l"
            label={label}
            feedbackContent={validations.input}
            testId="generic-setting-modal-textarea"
          >
            <Textarea
              plainText
              name="generic-setting"
              type="text"
              size="m"
              initialContent={inputValue}
              onChange={handleOnChange}
              placeholder={inputPlaceholder}
              ref={textareaRef}
              hasError={!!validations.input}
              testId="generic-setting-modal"
            />
          </InputField>
        ) : (
          <InputField
            size="l"
            label={label}
            feedbackContent={validations.input}
            testId="generic-setting-modal-input"
          >
            <Input
              size="l"
              value={inputValue}
              onChange={handleOnChange}
              placeholder={inputPlaceholder}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && validations.input === '') {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              ref={inputRef as MutableRefObject<HTMLInputElement>}
              hasError={!!validations.input}
              testId="generic-setting-modal"
            />
          </InputField>
        )}
      </Modal.Body>
      <Modal.Footer>
        {buttons?.length > 0 ? (
          renderButtons()
        ) : (
          <>
            <Button
              variant="standard"
              size="m"
              onClick={close}
              testId="generic-setting-modal-close"
            >
              <FormattedMessage id="global.close" />
            </Button>
            <Button
              size="m"
              disabled={!!validations.input}
              onClick={handleSubmit}
              style={{ margin: '0 0 0 1rem' }}
              testId="generic-setting-modal-save"
              variant="primary"
            >
              <FormattedMessage id="global.save" />
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default GenericSettingModal;
